import { Injectable } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { BehaviorSubject, combineLatest, filter, map, Observable, startWith } from 'rxjs';
import { PermissionService } from '../../core/permissions/permission.service';
import { HELP_SECTIONS } from './help-center.content';
import { HelpArticle, HelpSection } from './help-center.types';

export interface HelpTopic {
    section: HelpSection;
    article: HelpArticle;
}

/** Pantalla (ruta y, si aplica, pestaña ?tab=) → artículo de ayuda. Se usa la primera regla que encaja. */
interface HelpRule {
    path: RegExp;
    tab?: string;
    section: string;
    article: string;
}

const RULES: HelpRule[] = [
    { path: /^\/home$/, section: 'primeros-pasos', article: 'inicio' },
    { path: /^\/projects$/, section: 'proyectos', article: 'lista' },
    { path: /^\/projects\/\d+$/, tab: 'eventos', section: 'eventos', article: 'buscar' },
    { path: /^\/projects\/\d+$/, tab: 'solicitudes', section: 'deteccion-contenido', article: 'que-es' },
    { path: /^\/projects\/\d+$/, section: 'proyectos', article: 'pagina' },
    { path: /^\/honeytokens-management/, section: 'honeytokens', article: 'lista' },
    { path: /^\/honeytokens\/\d+$/, section: 'honeytokens', article: 'pagina' },
    { path: /^\/security-events\/\d+\/report$/, section: 'eventos', article: 'informe' },
    { path: /^\/security-events$/, section: 'eventos', article: 'buscar' },
    { path: /^\/alerts/, section: 'alertas', article: 'gestionar' },
    { path: /^\/threat-intelligence\/ip-analysis/, section: 'inteligencia', article: 'ips' },
    { path: /^\/threat-intelligence\/event-analysis/, section: 'inteligencia', article: 'eventos' },
    { path: /^\/detection-content\/requests\/\d+\/workflow$/, tab: 'honeytokens', section: 'deteccion-contenido', article: 'seguimiento' },
    { path: /^\/detection-content\/requests\/\d+\/workflow$/, tab: 'eventos', section: 'deteccion-contenido', article: 'seguimiento' },
    { path: /^\/detection-content\/requests\/\d+\/workflow$/, tab: 'estadisticas', section: 'deteccion-contenido', article: 'seguimiento' },
    { path: /^\/detection-content\/requests\/\d+\/workflow$/, section: 'deteccion-contenido', article: 'que-es' },
    { path: /^\/detection-content\/drafts\/\d+\/publish$/, section: 'deteccion-contenido', article: 'publicar' },
    { path: /^\/detection-content\/drafts\/\d+/, section: 'deteccion-contenido', article: 'revision' },
    { path: /^\/detection-content/, section: 'deteccion-contenido', article: 'que-es' },
    { path: /^\/glossary/, section: 'preguntas-frecuentes', article: 'glosario' },
    { path: /^\/users/, section: 'administracion', article: 'usuarios' },
    { path: /^\/administration\/domains/, section: 'administracion', article: 'dominios' },
    { path: /^\/administration/, section: 'administracion', article: 'catalogos' },
];

/**
 * Ayuda contextual: el artículo del Centro de ayuda que corresponde a la pantalla y pestaña actuales.
 * Una pantalla puede fijar otro más concreto mientras se muestra algo (p. ej. un aviso) con setOverride().
 */
@Injectable({ providedIn: 'root' })
export class HelpContextService {
    /** Artículo fijado por una pantalla, válido solo en la dirección (con su ?tab) en la que se fijó */
    private readonly _override = new BehaviorSubject<{ url: string; section: string; article: string } | null>(null);

    /** Artículo de la pantalla actual, o null si no tiene uno propio */
    readonly topic$: Observable<HelpTopic | null>;

    constructor(private _router: Router, private _permissionService: PermissionService) {
        const url$ = this._router.events.pipe(
            filter((event): event is NavigationEnd => event instanceof NavigationEnd),
            map((event) => event.urlAfterRedirects),
            startWith(this._router.url),
        );

        this.topic$ = combineLatest([url$, this._override]).pipe(
            map(([url, override]) => override && override.url === url
                ? this.find(override.section, override.article)
                : this.resolve(url)),
        );
    }

    /**
     * Fija el artículo de ayuda mientras la pantalla actual muestra algo concreto (p. ej. un aviso).
     * null lo quita. Solo vale en la dirección actual: en otra pestaña o pantalla se usa su artículo.
     */
    setOverride(section: string | null, article?: string): void {
        this._override.next(section && article ? { url: this._router.url, section, article } : null);
    }

    resolve(url: string): HelpTopic | null {
        const tree = this._router.parseUrl(url);
        const path = '/' + (tree.root.children['primary']?.segments.map((segment) => segment.path).join('/') ?? '');
        const tab = tree.queryParamMap.get('tab');

        const rule = RULES.find((candidate) => candidate.path.test(path) && (!candidate.tab || candidate.tab === tab));
        return rule ? this.find(rule.section, rule.article) : null;
    }

    private find(sectionId: string, articleId: string): HelpTopic | null {
        const section = HELP_SECTIONS.find((candidate) => candidate.id === sectionId);
        const article = section?.articles.find((candidate) => candidate.id === articleId);

        if (!section || !article || (section.adminOnly && !this._permissionService.isAdmin())) {
            return null;
        }

        return { section, article };
    }
}
